import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { formatDateTime } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY_FORM = { nome: '', email: '', role_id: '', loja_id: '' };

const Usuarios = () => {
  const { user: loggedUser } = useAuth();
  const [usuarios, setUsuarios]     = useState([]);
  const [lojas, setLojas]           = useState([]);
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editando, setEditando]     = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [uRes, lRes] = await Promise.all([
        api.get('/usuarios'),
        api.get('/lojas'),
      ]);
      setUsuarios(uRes.data);
      setLojas(lRes.data);
      // Extrai roles únicas dos usuários retornados como conveniência
      // (ou busca endpoint /roles se existir)
      const uniqueRoles = [];
      const seen = new Set();
      uRes.data.forEach((u) => {
        if (!seen.has(u.role)) {
          seen.add(u.role);
          uniqueRoles.push({ nome: u.role });
        }
      });
      setRoles(uniqueRoles.length ? uniqueRoles : [{ nome: 'Admin' }, { nome: 'Vendedor' }]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const abrirNovo = () => {
    setEditando(null);
    setForm(EMPTY_FORM);
    setError('');
    setModalOpen(true);
  };

  const abrirEditar = (u) => {
    setEditando(u);
    setForm({ nome: u.nome, email: u.email, role_id: '', loja_id: u.loja_id || '' });
    setError('');
    setModalOpen(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editando) {
        await api.put(`/usuarios/${editando.id}`, form);
      } else {
        await api.post('/usuarios', form);
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar usuário.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (u) => {
    try {
      await api.patch(`/usuarios/${u.id}/ativar`, { ativo: !u.ativo });
      setUsuarios((prev) =>
        prev.map((x) => x.id === u.id ? { ...x, ativo: !u.ativo } : x)
      );
    } catch (err) {
      console.error(err);
    }
  };

  const resetSenha = async (u) => {
    if (!window.confirm(`Resetar senha de ${u.nome} para "123"?`)) return;
    try {
      await api.post(`/auth/reset-password/${u.id}`);
      alert('Senha resetada com sucesso.');
    } catch (err) {
      alert('Erro ao resetar senha.');
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Usuários</h1>
          <p className="text-sm text-gray-500 mt-0.5">{usuarios.length} usuário(s)</p>
        </div>
        <button onClick={abrirNovo} className="btn-primary">
          <PlusIcon className="w-4 h-4" />
          Novo usuário
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : usuarios.length === 0 ? (
        <EmptyState title="Nenhum usuário cadastrado" />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Perfil</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Loja</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Último login</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {usuarios.map((u) => (
                  <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${!u.ativo ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{u.nome}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                      <span className={`badge ${u.role === 'Admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{u.loja_nome || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                      {formatDateTime(u.ultimo_login)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${u.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => abrirEditar(u)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                          aria-label="Editar"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        {u.id !== loggedUser?.id && (
                          <button
                            onClick={() => toggleAtivo(u)}
                            className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                              u.ativo
                                ? 'text-red-500 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                          >
                            {u.ativo ? 'Desativar' : 'Ativar'}
                          </button>
                        )}
                        <button
                          onClick={() => resetSenha(u)}
                          className="text-xs px-2 py-1 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                          Reset senha
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal criar/editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editando ? 'Editar usuário' : 'Novo usuário'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome <span className="text-red-500">*</span></label>
            <input name="nome" value={form.nome} onChange={handleChange} required className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail <span className="text-red-500">*</span></label>
            <input name="email" type="email" value={form.email} onChange={handleChange} required className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Perfil <span className="text-red-500">*</span></label>
            <select name="role_id" value={form.role_id} onChange={handleChange} required className="input">
              <option value="">Selecione...</option>
              <option value="1">Admin</option>
              <option value="2">Vendedor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Loja</label>
            <select name="loja_id" value={form.loja_id} onChange={handleChange} className="input">
              <option value="">Sem loja específica</option>
              {lojas.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
          {!editando && (
            <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              A senha inicial será <strong>123</strong>. O usuário deverá alterá-la no primeiro acesso.
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar usuário'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default Usuarios;
